/// <reference types="vitest" />
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  signInWithPopup,
  User,
  UserCredential,
} from "firebase/auth";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "integrations/firebase/auth";
import {
  fetchUserAccount,
  UserAccount,
} from "integrations/firebase/userAccount";
import { CHAPTR_ORGANISATION_ID } from "shared/constants";
import { GlobalLoading } from "components/loading";
import { logError } from "shared/services/ErrorReporting";
import { t } from "i18next";
import { toast } from "@/utils/toast";

export interface IAuthContext {
  authUser?: User | null;
  loading: boolean;
  error?: Error;
  isLoggedIn: boolean;
  isAccountLoading: boolean;
  organisationId?: string;
  isSuperAdmin: boolean;
  canSeePrototypes: boolean;

  signInWithOauth: (type: OAuthProviderType) => Promise<UserCredential | void>;

  sendLoginLink: (email: string) => Promise<void>;

  signOut: () => Promise<void>;
}

export const AuthContext = createContext<IAuthContext>({
  isLoggedIn: false,
  isAccountLoading: false,
  loading: true,
  isSuperAdmin: false,
  canSeePrototypes: false,
  sendLoginLink: async () => {},
  signInWithOauth: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const OAUTH_PROVIDERS = {
  google: new GoogleAuthProvider(),
};

type OAuthProviderType = keyof typeof OAUTH_PROVIDERS;

export interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  userId: string;
  photoUrl?: string;
}

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const [initialized, setInitialized] = useState(false);
  const [authUser, loading] = useAuthState(auth);
  const [error, setError] = useState<Error>();
  const [account, setAccount] = useState<UserAccount>();
  const [accountUid, setAccountUid] = useState<string>();

  const signOut = useCallback(() => auth.signOut(), []);

  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;
    fetchUserAccount(authUser.uid)
      .then((loaded) => {
        if (cancelled) return;
        if (!loaded) {
          toast.error(t("login:error.noOrganisation"));
          auth.signOut();
          return;
        }
        setAccount(loaded);
        setAccountUid(authUser.uid);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e as Error);
        auth.signOut();
      });
    return () => {
      cancelled = true;
    };
  }, [authUser]);

  useEffect(() => {
    if (loading) {
      return;
    }

    setInitialized(true);
  }, [authUser, loading]);

  useEffect(() => {
    if (error) {
      logError(error);
      toast.error(`${t("login:error.loginFailed")}: ${error.message}`);
      setError(undefined);
    }
  }, [error]);

  const signInWithOauth = useCallback(async (type: OAuthProviderType) => {
    const provider = OAUTH_PROVIDERS[type];

    if (!provider) {
      throw new Error(`No provider found for type ${type}`);
    }

    try {
      const result = await signInWithPopup(auth, provider);
      toast.success(t("login:success.toast.title"));
      return result;
    } catch (e) {
      setError(e as Error);
    }
  }, []);

  const sendLoginLink = useCallback(async (email: string) => {
    try {
      window.localStorage.setItem("emailForSignIn", email);

      await sendSignInLinkToEmail(auth, email, {
        handleCodeInApp: true,
        url: `${window.location.origin}/login-link`,
      });

      toast.success(t("login:link.success.linkSent", { email }));
    } catch (e) {
      setError(e);
      window.localStorage.removeItem("emailForSignIn");
    }
  }, []);

  const isLoggedIn = !!authUser;
  const isAccountLoading = !!authUser && accountUid !== authUser.uid;
  const organisationId = isLoggedIn ? account?.organisationId : undefined;
  const isSuperAdmin = isLoggedIn && !!account?.roles.includes("SUPER_ADMIN");
  const canSeePrototypes =
    organisationId === CHAPTR_ORGANISATION_ID || isSuperAdmin;
  const authProviderValue = useMemo(
    () => ({
      authUser,
      loading,
      error,
      isLoggedIn,
      isAccountLoading,
      organisationId,
      isSuperAdmin,
      canSeePrototypes,
      signInWithOauth,
      signOut,
      sendLoginLink,
    }),
    [
      authUser,
      loading,
      error,
      isLoggedIn,
      isAccountLoading,
      organisationId,
      isSuperAdmin,
      canSeePrototypes,
      signInWithOauth,
      signOut,
      sendLoginLink,
    ],
  );

  if (loading || !initialized) {
    return <GlobalLoading />;
  }

  return <AuthContext value={authProviderValue}>{children}</AuthContext>;
};
